import SelectSearch, {
  fuzzySearch,
  SelectedOptionValue,
  SelectedOption,
  SelectSearchOption,
  SelectSearchProps,
} from 'react-select-search';
import { Flex } from 'reflexbox';
import { useStateVar } from 'helpers/useStateVar';
import { colors } from '~/styles/colors';
import { SingleDatePicker } from 'react-dates';
import moment, { Moment } from 'moment';
import { InputSearch } from '~/components/NewInputs/Search';
import {
  SearchInput,
  Label,
  ControlFilter,
  CleanBtn,
  FiltersContainer,
  ContainerArea,
  LoadingContainer,
  ContentDate,
  DateLabel,
  StyledCalendarIcon,
  ContainerSearch,
} from './styles';
import i18n from '~/i18n';
import { Button, Loader } from '~/components';

import {
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { FilterIcon } from '~/icons';
import { getUserProfile } from '~/helpers/userProfile';
import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import {
  getClientsList,
  getUnitsList,
  getStatesList,
  getCitiesList,
  getUnitStatus,
  getReasonsOfflineStatusPopulateState,
  getApplicationsPopulateState,
  getDeviceStatus,
} from '~/helpers/genericHelper';
import { getReasonsList } from '~/helpers/remoteControlHelper';

const t = i18n.t.bind(i18n);

export interface FilterOption {
  value: string | number;
  name: string;
  clientId?: number;
  unitId?: number;
}

export interface FilterConfig {
  type: 'select' | 'date' | 'multi-select' | 'searchable-select';
  key: 'client' | 'unit' | 'motivoAlteracao' | 'alteracao' | 'state' | 'city' | 'unitStatus' | 'deviceStatus' | 'reasons' | 'application'; // ao usar o componente adicione sua chave
  label: string;
  placeholder: string;
  options?: FilterOption[] | (() => FilterOption[]) | (() => Promise<FilterOption[]>);
  multiple?: boolean;
  dependencies?: string[]; // Outros filtros dos quais este depende
  filterFunction?: (options: FilterOption[], selectedDependencies: Record<string, any>) => FilterOption[];
  dateConfig?: {
    isOutsideRange?: (date: Moment) => boolean;
  };
  searchable?: boolean; // Para select com busca livre
  isVisible?: boolean;
}

export interface FilterState {
  [key: string]: any;
}

interface GenericFilterProps {
  state: any;
  onApply: () => void;
  updateInfos: (topic: string, newValues?: any[], dates?: Moment | null) => void;
  filters: FilterConfig[]; // Configuração dos filtros
}

// Status que devem ser sincronizados entre Unidade e Dispositivo
const SYNCHRONIZED_STATUSES = ['OPERATION', 'FORCED_UNINSTALL'];

export const GenericFilter = (props: GenericFilterProps): JSX.Element => {
  const [state, render, setState] = useStateVar(() => {
    const initialState: FilterState = {
      isLoading: false,
      showFilter: true,
      finalizedInputs: false,
      isClearFilter: false,
      tomorrow: moment(moment().add(1, 'days').format('YYYY-MM-DD')),
      focused: {} as { id: string; focused: boolean },
      alredyCallFilterReasonAndChange: false,
      user: '',
    };

    // Inicializar estados dinâmicamente baseado na configuração dos filtros
    props.filters.forEach((filter) => {
      if (filter.type === 'date') {
        initialState[filter.key] = null;
        initialState[`${filter.key}Focused`] = false;
      } else {
        initialState[filter.key] = filter.multiple ? [] : '';
      }

      // Estados para options quando aplicável
      if (filter.type === 'select' || filter.type === 'multi-select' || filter.type === 'searchable-select') {
        initialState[`${filter.key}Options`] = [];
        if (filter.dependencies?.length) {
          initialState[`${filter.key}FilteredOptions`] = [];
        }
      }
    });

    return initialState;
  });

  const [profile] = useState(getUserProfile);

  // Função para buscar opções de um filtro
  const fetchFilterOptions = async (filterConfig: FilterConfig) => {
    if (!filterConfig.options) return [];
    if (typeof filterConfig.options === 'function') {
      const result = await filterConfig.options();
      return Array.isArray(result) ? result : [];
    }
    return filterConfig.options;
  };

  // Função para inicializar dados dos filtros
  const initializeFiltersData = async () => {
    for (const filter of props.filters) {
      if (filter.type === 'select' || filter.type === 'multi-select' || filter.type === 'searchable-select') {
        try {
          let options: FilterOption[] = [];
          if (['alteracao', 'motivoAlteracao'].includes(filter.key) && !state.alredyCallFilterReasonAndChange) {
            await getReasonsList(state, render);
            setState({ alredyCallFilterReasonAndChange: true });
          }

          switch (filter.key) {
            case 'client':
              if (filter.isVisible) {
                await getClientsList(state, render);
                options = state.clientsListOpts || [];
              }
              break;
            case 'unit':
              await getUnitsList(state, render);
              options = state.unitsListOpts || [];
              break;
            case 'state':
              await getStatesList(state, render);
              options = state.statesListOpts || [];
              break;
            case 'city':
              await getCitiesList(state, render);
              options = state.citiesListOpts || [];
              break;
            case 'unitStatus':
              getUnitStatus(state, render);
              options = state.unitStatusListOpts || [];
              break;
            case 'deviceStatus':
              getDeviceStatus(state, render);
              options = state.deviceStatusListOpts || [];
              break;
            case 'reasons':
              getReasonsOfflineStatusPopulateState(state, render);
              options = state.reasonsListOpts || [];
              break;
            case 'application':
              getApplicationsPopulateState(state, render);
              options = state.applicationsListOpts || [];
              break;
            case 'motivoAlteracao':
              options = state.reasonsListOpts || [];
              break;
            case 'alteracao':
              options = state.changeListOpts || [];
              break;
            default:
              options = await fetchFilterOptions(filter);
          }

          setState({
            [`${filter.key}Options`]: options,
            [`${filter.key}FilteredOptions`]: options,
          });
        } catch (error) {
          console.error(`Error loading options for ${filter.key}:`, error);
        }
      }
    }
  };

  // Função para aplicar filtros de dependência
  const applyDependencyFilters = (filterConfig: FilterConfig) => {
    if (!filterConfig.dependencies?.length || !filterConfig.filterFunction) {
      return;
    }

    const selectedDependencies: Record<string, any> = {};
    filterConfig.dependencies.forEach((dep) => {
      selectedDependencies[dep] = state[dep];
    });

    const originalOptions = state[`${filterConfig.key}Options`] || [];
    const filteredOptions = filterConfig.filterFunction(originalOptions, selectedDependencies);

    setState({
      [`${filterConfig.key}FilteredOptions`]: filteredOptions,
      [filterConfig.key]: filterConfig.multiple ? [] : '', // Reset selection
    });
  };

  // Função genérica para mudança de valor
  const handleFilterChange = (filterKey: string, value: any) => {
    const updates: Partial<FilterState> = { [filterKey]: value };

    // Sincronização especial: unitStatus -> deviceStatus
    if (filterKey === 'unitStatus') {
      const newUnitStatuses = Array.isArray(value) ? value : [];
      const oldUnitStatuses = Array.isArray(state.unitStatus) ? state.unitStatus : [];

      const added = newUnitStatuses.filter((s: string) => !oldUnitStatuses.includes(s));
      const removed = oldUnitStatuses.filter((s: string) => !newUnitStatuses.includes(s));

      const deviceStatusesToAdd = added.filter((status: string) => SYNCHRONIZED_STATUSES.includes(status));
      const deviceStatusesToRemove = removed.filter((status: string) => SYNCHRONIZED_STATUSES.includes(status));

      const currentDeviceStatuses = Array.isArray(state.deviceStatus) ? state.deviceStatus : [];
      const updatedDeviceStatuses = [
        ...currentDeviceStatuses.filter((ds: string) => !deviceStatusesToRemove.includes(ds)),
        ...deviceStatusesToAdd,
      ];

      updates.deviceStatus = Array.from(new Set(updatedDeviceStatuses));
    }

    setState(updates);

    // Aplicar filtros dependentes
    props.filters.forEach((filter) => {
      if (filter.dependencies?.includes(filterKey)) {
        applyDependencyFilters(filter);
      }
    });

    render();
  };

  // Função para definir valores no estado pai
  const setValues = () => {
    props.filters.forEach((filter) => {
      if (filter.type === 'date') {
        props.updateInfos(filter.key, undefined, state[filter.key]);
      } else {
        props.updateInfos(filter.key, state[filter.key]);
      }
    });
  };

  // Função para limpar filtros
  const clearFilters = () => {
    const clearedState: Partial<FilterState> = { isClearFilter: true };

    props.filters.forEach((filter) => {
      if (filter.type === 'date') {
        clearedState[filter.key] = null;
        clearedState[`${filter.key}Focused`] = false;
      } else {
        clearedState[filter.key] = filter.multiple ? [] : '';
      }

      // Reset filtered options to original options
      if (filter.dependencies?.length) {
        clearedState[`${filter.key}FilteredOptions`] = state[`${filter.key}Options`];
      }
    });

    setState(clearedState);
    setValues();
    render();
    sessionStorage.clear();
    props.state.needFilter = null;
    state.isLoading = false;
  };

  // Renderizar filtro baseado no tipo
  const renderFilter = (filterConfig: FilterConfig) => {
    const {
      type, key, label, placeholder, multiple, dateConfig,
    } = filterConfig;
    const options = (filterConfig.dependencies?.length && type === 'multi-select') ? (state[`${key}FilteredOptions`] || []) : (state[`${key}Options`] || []);
    switch (type) {
      case 'date':
        return (
          <ContentDate key={key}>
            <DateLabel>{label}</DateLabel>
            <SingleDatePicker
              disabled={state.isLoading}
              date={state[key]}
              focused={state[`${key}Focused`]}
              onFocusChange={({ focused }) => setState({ [`${key}Focused`]: focused })}
              onDateChange={(date) => handleFilterChange(key, date)}
              numberOfMonths={1}
              displayFormat="DD/MM/YYYY"
              isOutsideRange={dateConfig?.isOutsideRange || ((d) => !d.isBefore(state.tomorrow))}
              placeholder={placeholder}
            />
            <StyledCalendarIcon color={colors.Blue700} />
          </ContentDate>
        );

      case 'select':
      case 'multi-select':
        return (
          <FilterOption
            key={key}
            options={options}
            value={state[key]}
            onChange={(value) => handleFilterChange(key, value)}
            multiple={multiple}
            placeholder={placeholder}
            disabled={state.isLoading}
          />
        );

      case 'searchable-select':
        return (
          <ContainerSearch>
            <InputSearch
              key={key}
              style={{
                height: '41px', width: 224, fontSize: '11px', borderRadius: '8px', border: '1px solid #BABABA', minHeight: '41px',
              }}
              id="search"
              name="search"
              label={label}
              placeholder={placeholder}
              value={state[key]}
              onChange={(id) => { state[key] = id; render(); }}
              disabled={state.isLoading}
              filterStyle
              noBorder
            />
          </ContainerSearch>
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    setState({ finalizedInputs: true });
    initializeFiltersData();
  }, []);

  useEffect(() => {
    const syncedState: Partial<FilterState> = {};
    let hasChanges = false;

    props.filters.forEach((filter) => {
      const keyInParentState = `selected${filter.key.charAt(0).toUpperCase()}${filter.key.slice(1)}Opts`;
      const parentValue = props.state[keyInParentState];

      if (parentValue !== undefined && parentValue !== state[filter.key]) {
        syncedState[filter.key] = Array.isArray(parentValue)
          ? [...parentValue]
          : parentValue;
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setState(syncedState);
    }
  }, [props.state.selectedStateOpts, props.state.selectedUnitOpts, props.state.selectedCityOpts, props.state.selectedUnitStatusOpts, props.state.selectedDeviceStatusOpts]);

  return (
    <Flex style={{ position: 'relative' }} flexDirection="column">
      <div style={{ display: 'flex', alignItems: 'center', margin: '10px 0px' }}>
        <ControlFilter onClick={() => { state.showFilter = !state.showFilter; render(); }}>
          <FilterIcon />
          {t('filtros')}
          {state.showFilter ? <ChevronUpIcon width={20} color={colors.BlueSecondary} /> : <ChevronDownIcon width={20} color={colors.BlueSecondary} />}
        </ControlFilter>
        <div>
          <CleanBtn onClick={() => { clearFilters(); props.onApply(); }}>
            {t('limparFiltros')}
          </CleanBtn>
        </div>
      </div>

      {state.finalizedInputs ? (
        <FiltersContainer
          flexWrap={['wrap', 'wrap', 'wrap', 'nowrap', 'nowrap']}
          showFilter={state.showFilter}
          style={{ position: 'relative', marginBottom: 20 }}
        >
          {state.showFilter && (
            <Flex
              flexDirection="row"
              justifyContent="center"
              mt={state.showFilter ? 0 : -23}
              alignItems="flex-start"
              style={{
                transition: 'visibility 0.5s, opacity 0.5s, background-color 0.5s, margin-top 1s',
                visibility: state.showFilter ? 'visible' : 'hidden',
                opacity: state.showFilter ? '1' : '0',
              }}
              flexWrap={['wrap', 'wrap', 'wrap', 'wrap', 'nowrap']}
            >
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                  {props.filters.map((filter) => filter.isVisible && renderFilter(filter))}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'end',
                  marginTop: 'auto',
                  justifyContent: 'end',
                }}
              >
                <Button
                  style={{
                    width: '150px',
                    marginLeft: '20px',
                    minWidth: '150px',
                    height: '41px',
                    textTransform: 'capitalize',
                    borderRadius: '4px',
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  type="button"
                  variant="primary"
                  onClick={() => { setValues(); props.onApply(); render(); }}
                >
                  {t('analisar')}
                </Button>
              </div>
            </Flex>
          )}
        </FiltersContainer>
      ) : (
        <LoadingContainer>
          <Loader />
        </LoadingContainer>
      )}
    </Flex>
  );
};

interface SelectSearchOptionProps extends SelectSearchOption {
  icon?: ReactNode;
}

const FilterOption = (props: {
  value: string | string[] | undefined;
  placeholder: string;
  onChange: ((selectedValue: SelectedOptionValue | SelectedOptionValue[], selectedOption: SelectedOption | SelectedOption[], optionSnapshot: SelectSearchProps) => void) | undefined;
  options: SelectSearchOptionProps[];
  disabled?: boolean;
  multiple?: boolean;
  closeOnSelected?: boolean;
  removeMarginBottom?: boolean;
}) => (
  <SearchInput
    disabled={props.disabled}
    style={{
      width: '226px',
      marginBottom: props.removeMarginBottom ? '0' : '10px',
      borderRadius: '8px',
      height: '41px',
      padding: '2px 0px',
    }}
  >
    <ContainerArea>
      <Label style={{ opacity: props.disabled ? '0.4' : '1' }}>{props.placeholder}</Label>
      <SelectSearch
        options={props.options}
        value={props.value}
        multiple={props.multiple}
        closeOnSelect={props.closeOnSelected}
        printOptions="on-focus"
        search
        filterOptions={fuzzySearch}
        placeholder={t('selecionar')}
        onChange={props.onChange}
        disabled={props.disabled}
        emptyMessage="Nenhuma opção encontrada"
      />
    </ContainerArea>
  </SearchInput>
);
